import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import handleHospital from "@/services/modules/hospital";
import useDate from '@/services/utils/day';
import { chunkArray, formatDateToDdMmYy, joinArrayOfString } from '@/services/utils/global';
import { startCase, head, last } from 'lodash';

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const pdfPrinter = () => {
    const { formatDate } = useDate();
    const { calculateAge } = handleHospital();

    const exportToPDF = (company, admissionInfo, dischargeNotes, formData, qrcode, userName, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder = null) => {
        var doc = {
            pageSize: 'A4',
            pageMargins: [ 75, 150, 25, 48 ],
            header: {},
            footer: {},
            content: getContent(doc, admissionInfo, dischargeNotes, formData, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder),
            
            styles : {
                header: {
                    fontSize: 24,
                }
            },

            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },

            info: {
                title: 'DischaregeOrder'
            }
        }

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getContent = (doc, admissionInfo, dischargeNotes, formData, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder) => {
        return [
            {
                text: 'Discharge Certificate',
                style: 'header',
                alignment: 'center',
                margin: [0, 10, 20, 10],
                bold: true,
                color: 'black',
            },

            {
                margin: [0, 20, 0, 0],
                alignment: 'justify',
                columns: [
                    {
                        width: '50%',
                        stack: [
                            {
                                svg: barcodePatient
                            },

                            {
                                text: `Patient Name: ${admissionInfo.patient.full_name}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `Patient ID: ${admissionInfo.patient.serial_no}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `${(admissionInfo.patient && admissionInfo.patient.birthday) ? 
                                        'Age: ' + calculateAge(admissionInfo.patient.birthday) : ''}` + 
                                        `${(admissionInfo.patient && admissionInfo.patient.gender) ? 
                                        ' | Sex: ' + admissionInfo.patient.gender : '' }`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `Patient Phone No: ${admissionInfo.patient.mobile_no}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `${admissionInfo.patient.full_address ? 'Patient Address: ' + admissionInfo.patient.full_address : ''}`,
                                alignment: 'left',
                                width: '50%'
                            },
                        ]
                    },

                    {
                        alignment: 'right',
                        stack: [
                            {
                                svg: barcodeAdmission
                            },

                            {
                                text: `Admission No: ${admissionInfo.admission_no}`
                            },

                            {
                                text: `Admission Date: ${admissionInfo.formattedAdmissionData}`
                            },

                            {
                                text: `Discharge Date: ${formatDate(new Date(formData.dischargeDate))}`
                            }
                        ]
                    },
                ]
            },

            {
                canvas: [
                    {
                        type: 'line',
                        x1: 0, 
                        y1: 0, 
                        x2: 400,
                        y2: 0,
                    },
                ],
                margin: [0, 5]
            },

            {
                margin: [0, 5], 
                columns: [
                    admissionInfo.human_resource ? {
                        width: "33%",
                        stack: [
                            {
                                text: 'Referred Doctor',
                                fontSize: 12,
                                bold: true,
                                decoration: 'underline',
                                margin: [2, 5]
                            },
                            {
                                text: admissionInfo.human_resource.name
                            }
                        ]
                    } : '',

                    admissionInfo.admitted_by_user ? {
                        width: "33%",
                        stack: [
                            {
                                text: 'Admitting Doctor',
                                fontSize: 12,
                                bold: true,
                                decoration: 'underline',
                                margin: [2, 5]
                            },
                            {
                                text: admissionInfo.admitted_by_user.full_name
                            }
                        ]
                    } : '',
        
                    treatedByDoctors ? {
                        width: "33%",
                        stack: [
                            {
                                text: 'Treating Doctors',
                                fontSize: 12,
                                bold: true,
                                decoration: 'underline',
                                margin: [2, 5]
                            },
                            {
                                text: treatedByDoctors
                            }
                        ]
                    }: ''
                ]
            },

            getNotesOnAdmission(admissionInfo, formData, treatedByDoctors),

            ...( notesOrder.investigations.length ? pushIntoTable(doc, notesOrder.investigations) : [] ),
            
            getDischargeMode(dischargeModes, formData),

            getOtNotes(notesOrder.otNotes),

            getBabyNotes(notesOrder.babyNotes),

            {
                text: 'Discharge Medications',
                fontSize: 12,
                bold: true,
                decoration: 'underline',
                margin: [0, 5]
            },

            getRx(dischargeNotes.rx.selectedNotes),

            getFollowUp(dischargeNotes.follow_up.selectedNotes),

            getInvestigation(dischargeNotes.investigation.selectedNotes),

            getDisChargeNotes(dischargeNotes.discharge_note.selectedNotes),

            serviceDoctorOrder && serviceDoctorOrder.composed_service_name ? {
                margin: [0, 25, 0, 5],
                unbreakable: true,
                stack: [
                    {
                        text: 'Discharge Composed By: ',
                        fontSize: 12,
                        bold: true,
                        decoration: 'underline',
                        margin: [0, 5]
                    },
                    {
                        text: serviceDoctorOrder.composed_service_name
                    }
                ]
            } : ''
        ]
    }

    const getDischargeMode = (dischargeModes, formData) => {
        const singleMode = dischargeModes.find(item => item.value === formData.mode_of_discharge);
        if(!singleMode) return '';

        return {
            margin: [0, 5],
            unbreakable: true,
            columns: [
                {
                    width: '20%',
                    stack: [
                        {
                            text: 'Mode of Discharge: ',
                            fontSize: 12,
                            bold: true,
                        }
                    ]
                },
                {
                    width: '80%',
                    stack: [
                        {
                            text: singleMode.name,
                            fontSize: 12,
                            margin: [0, 1, 0, 0]
                        }
                    ]
                },
            ]
        }
    }

    const pushIntoTable = (doc, investigations) => {
        const tableTitle = getInvestigationTitle();
        const tableHeader = getTableHeader()
        
        const content = {
            style: 'tableExample',
            margin: [0, 5],
            table: {
                widths: ['*', '15%', '15%', '15%', '15%', '15%'],
                body: []
            },
            layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                  return '';
                },
                hLineWidth: function (i, node) {
                  return 0.5;
                },
                vLineWidth: function (i, node) {
                  return 0;
                },
                hLineColor: function (i, node) {
                  return ''; 
                },
                vLineColor: function (i, node) {
                  return (i === 0 || i === node.table.widths.length) ? '#000000' : '#dddddd';
                },
            }
        }

        content.table.body.push(tableHeader);

        for(const item of investigations){
            content.table.body.push(getRowData(item))
        }

        return [
            tableTitle,
            content
        ];
    }

    const getInvestigationTitle = () => {
        return {
            width: "50%",
            text: 'Investigation Details',
            fontSize: 12,
            bold: true,
            decoration: 'underline',
            margin: [0, 5, 0, 0]
        }
    }

    const getRowData = (item) => {
        return [ 
            { text: item.latestResult.product_name || item.oldResult.product_name },
            { text: item.latestResult.name || item.oldResult.name  },
            { text: item.latestResult.unit || item.oldResult.unit },
            { text: item.oldResult.result },
            { text: item.otherResults.length ? item.otherResults[0].result : '' },
            { text: item.otherResults.length > 1 ? last(item.otherResults).result : '' } 
        ];
    }

    const getTableHeader = () => {
        return [
            { text: 'Name of investigation', bold: true },
            { text: 'Investigation Head', bold: true },
            { text: 'Unit', bold: true },
            { text: 'Previous Result', bold: true },
            { text: 'After Admission', bold: true },
            { text: 'During Discharge', bold: true }
        ];
    }

    const getRx = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [5, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'Rx',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes.map((el) => {
                        return `${el.name} \n ${ el.dose ? el.dose : ''} \t ${el.unit ? el.unit : ''} \t ${el.remarks ? el.remarks : ''} \t ${el.duration ? el.duration : ''}`;
                    })
                }
            ]
        };
    }

    const getFollowUp = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [5, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'OPD Follow up visit',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes
                }
            ]
        };
    }

    const getInvestigation = (notes) => {
        if(notes.length < 1) return '';

        return {
            margin: [0, 5, 0, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'Investigation Required',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes.map((el) => {
                        return el.name;
                    })
                }
            ]
        }
    }

    const getBabyNotes = (babyNotes) => {

        if(babyNotes.length < 1) return '';

        const formattedBabyNotes = {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'Baby Notes',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                }
            ]
        };

        const chunkNotes = chunkArray(babyNotes, 2);
        const formattedNotes = [];

        for(const notes of chunkNotes){
            const newNotes = notes.map((note) => {

                return {
                    width: '50%',
                    stack: [
                        {
                            text: `Gender: ${ startCase(note.gender) }`,
                            color: 'black',
                            bold: true,
                        },
                        {
                            text: `Weight: ${ note.weight }`,
                            color: 'black',
                            bold: true,
                        },
                        note.apgar_score ? {
                            text: `APGAR Score: ${ note.apgar_score}`,
                            color: 'black',
                            bold: true,
                        } : '',
                        note.notes.length ? {
                            text: `Special Note: ${ convertArrayToString(note.notes) }`,
                            color: 'black',
                            bold: true,
                        } : ''
                    ]
                }
            })
            
            formattedNotes.push({margin: [0, 5], columns: newNotes})
        }

        formattedBabyNotes.stack.push(... formattedNotes);
        return formattedBabyNotes;
    }

    const convertArrayToString = (notes) => {
        const note = notes.map(obj => obj.note).flat();
        return joinArrayOfString(note);
    }

    const getOtNotes = (otNotes) => {
        
        if(otNotes.length < 1) return '';

        const chunkNotes = chunkArray(otNotes, 2);

        const formattedNotes = {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'OT Notes',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                }
            ]
        }

        for(const notes of chunkNotes){
            const newNotes = notes.map((note) => {

                const consultants = formatOTNote(note);

                return {
                    width: '50%',
                    stack: [
                        {
                            text: `OT Date & Time: ${note.operation_date}`,
                            color: 'black',
                            bold: true,
                        },
                        {
                            text: `Name of Operation: ${note.name_of_operation}`,
                            color: 'black',
                            bold: true,
                        },
                        note.indication ? {
                            text: `Indication: ${startCase(note.indication)}`,
                            color: 'black',
                            bold: true,
                        } : '',
                        note.special_finding ? {
                            text: `Special Finding: ${ note.special_finding }`,
                            color: 'black',
                            bold: true,
                        } : '',
                        consultants.surgeon.length ? {
                            text: `Surgeon: ${joinArrayOfString(consultants.surgeon)}`,
                            color: 'black',
                            bold: true,
                        } : '',
                        consultants.ass_surgeon.length ? {
                            text: `Asst. Surgeon: ${joinArrayOfString(consultants.ass_surgeon)}`,
                            color: 'black',
                            bold: true,
                        } : '',
                        consultants.anestesiologist.length ? {
                            text: `Anestesiologist: ${joinArrayOfString(consultants.anestesiologist)}`,
                            color: 'black',
                            bold: true,
                        } : '',
                        consultants.nurse.length ? {
                            text: `Nurse: ${joinArrayOfString(consultants.nurse)}`,
                            color: 'black',
                            bold: true,
                        } : ''
                    ]
                }
            })

            formattedNotes.stack.push({margin: [0, 5], columns: newNotes})
        }

        return formattedNotes;
    }

    const formatOTNote = (note) => {
        const consultants = {
            surgeon: [],
            anestesiologist: [],
            ass_surgeon: [],
            nurse: [],
        };
        
        for(const item of note.ot_order_service){
            consultants[item.pivot.type].push(item.name);
        }

        return consultants;
    }

    const getNotesOnAdmission = (admissionInfo, formData, treatedByDoctors) => {
        return {
            margin: [0, 5],
            unbreakable: true,
            columns: [
                {
                    width: '100%',
                    stack: [
                        {
                            text: 'Notes on Admission',
                            fontSize: 12,
                            bold: true,
                            decoration: 'underline',
                            margin: [0, 5]
                        },
                        {
                            text: `Admitted Department: ${admissionInfo.department ? admissionInfo.department.name : ''}`
                        },
                        {
                            text: `Ward / bed / Cabin no: ${admissionInfo.service_resource.name}`
                        },
                        {
                            text: `Admitted date & time: ${admissionInfo.formattedAdmissionData}`
                        },
                        {
                            text: `Suffering from: ${formData.suffering_from || ''}`
                        } 	
                    ]
                },
            ]
        }
    }

    const getDisChargeNotes = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'Discharge Note',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                    margin: [0, 5]
                },
                {
                    margin: [0, 5],
                    ol: notes
                }
            ]
        }
    }

    const getHeader = (company) => {
        return {
            margin: [ 100, 70, 60, 10 ],
            columns: [
                {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                },

                {
                    alignment: 'right',
                    stack: [
                        company.name,
                        'Address: ' + company.address,
                        'Phone: ' + company.phone,
                        'E-mail: ' + company.email
                    ]
                }
            ]
        }
    }

    const getFooter = (userName, qrcode) =>{
        return {
            margin: [ 120, -30, 72, 96 ],
            columns: [
                {
                    columns: [
                        {
                            svg: qrcode
                        },

                        {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 40, 0, 0],
                            fontSize: 8
                        }
                    ]
                },

                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                }
            ]
        }
    }

    return {
        exportToPDF
    }
}

export default pdfPrinter;
